import { useState, useImperativeHandle, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { projectService, type Project, projectLabel } from '@/services/projectService'

interface DeleteProjectModalProps {
  canDirectDelete: boolean // platform owner or project owner
  canRequestDelete: boolean // platform owner or project owner or project admin
  onSuccess?: () => void
}

export interface DeleteProjectModalRef {
  open: (project: Project) => void
  close: () => void
}

const DeleteProjectModal = forwardRef<DeleteProjectModalRef, DeleteProjectModalProps>(
  ({ canDirectDelete, canRequestDelete, onSuccess }, ref) => {
    const { t } = useTranslation('projectInformation')
    const navigate = useNavigate()
    const [isOpen, setIsOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [success, setSuccess] = useState(false)
    const [project, setProject] = useState<Project | null>(null)
    const [actionType, setActionType] = useState<'direct' | 'request'>('direct')

    useImperativeHandle(ref, () => ({
      open: (projectData: Project) => {
        setProject(projectData)
        // If can direct delete, default to direct; otherwise request
        setActionType(canDirectDelete ? 'direct' : 'request')
        setIsOpen(true)
        setError('')
        setSuccess(false)
      },
      close: () => {
        setIsOpen(false)
        setProject(null)
        setActionType('direct')
        setError('')
        setSuccess(false)
      },
    }))

    const handleSubmit = async () => {
      if (!project || loading) return

      try {
        setLoading(true)
        setError('')

        if (actionType === 'direct') {
          // Direct delete project
          await projectService.deleteProject(project.id)
        } else {
          // Submit request
          await projectService.requestProjectDelete(project.id)
        }

        // Show success message instead of closing immediately
        setSuccess(true)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : t('deleteModal.operationFailed')
        setError(errorMessage)
      } finally {
        setLoading(false)
      }
    }

    const handleClose = () => {
      const wasSuccess = success
      const wasDirectDelete = actionType === 'direct'
      setIsOpen(false)
      setProject(null)
      setActionType('direct')
      setError('')
      setSuccess(false)

      // Only call onSuccess if the operation was successful
      if (wasSuccess) {
        onSuccess?.()
        // If directly deleted, navigate to projects page
        if (wasDirectDelete) {
          navigate('/projects')
        }
      }
    }

    const getStatusColor = (status: string) => {
      switch (status.toLowerCase()) {
        case 'active':
          return 'bg-green-100 text-green-800 border-green-200'
        case 'inactive':
          return 'bg-gray-100 text-gray-800 border-gray-200'
        case 'archived':
          return 'bg-yellow-100 text-yellow-800 border-yellow-200'
        default:
          return 'bg-gray-100 text-gray-800 border-gray-200'
      }
    }

    if (!isOpen || !project) return null

    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white/30 backdrop-blur-md">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4">
          {/* Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-red-600">
                  {t('deleteModal.title')}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {t('deleteModal.subtitle')}
                </p>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                disabled={loading}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            {/* Success Message */}
            {success && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-green-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-green-900 mb-1">
                      {actionType === 'direct'
                        ? t('deleteModal.successDirect')
                        : t('deleteModal.successRequest')}
                    </p>
                    <p className="text-xs text-green-700">
                      {actionType === 'direct'
                        ? t('deleteModal.successDirectDesc')
                        : t('deleteModal.successRequestDesc')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && !success && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <svg className="w-5 h-5 text-red-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Warning Message */}
            {!success && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-red-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-red-900 mb-1">
                      {actionType === 'direct' ? t('deleteModal.confirmDirect') : t('deleteModal.confirmRequest')}
                    </p>
                    <p className="text-xs text-red-700">
                      {actionType === 'direct'
                        ? t('deleteModal.warningDirect')
                        : t('deleteModal.warningRequest')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Project Info */}
            {!success && (
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-3">
                  {t('deleteModal.projectToDelete')}
                </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">{t('deleteModal.projectName')}</span>
                    <span className="text-sm text-gray-900">{projectLabel(project)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">{t('deleteModal.projectId')}</span>
                    <span className="text-sm text-gray-900 font-mono">{project.id}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">{t('deleteModal.projectStatus')}</span>
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${getStatusColor(project.status)}`}>
                      {t(`status.${project.status}`, project.status)}
                    </span>
                  </div>
                  {project.description && (
                    <div className="flex items-start gap-2">
                      <span className="text-sm font-medium text-gray-700">{t('deleteModal.projectDescription')}</span>
                      <span className="text-sm text-gray-900">{project.description}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Action Type Selection (Only if can do both) */}
            {!success && canDirectDelete && canRequestDelete && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('deleteModal.actionType')}
                </label>
                <div className="flex gap-3">
                  <label className="flex-1 cursor-pointer">
                    <input
                      type="radio"
                      name="actionType"
                      value="direct"
                      checked={actionType === 'direct'}
                      onChange={(e) => setActionType(e.target.value as 'direct' | 'request')}
                      disabled={loading}
                      className="sr-only"
                    />
                    <div className={`px-4 py-3 border-2 rounded-lg transition-all ${
                      actionType === 'direct'
                        ? 'border-red-600 bg-red-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}>
                      <span className="text-sm font-medium text-gray-900 block">
                        {t('deleteModal.directDelete')}
                      </span>
                      <p className="text-xs text-gray-600 mt-1">
                        {t('deleteModal.directDeleteDesc')}
                      </p>
                    </div>
                  </label>
                  <label className="flex-1 cursor-pointer">
                    <input
                      type="radio"
                      name="actionType"
                      value="request"
                      checked={actionType === 'request'}
                      onChange={(e) => setActionType(e.target.value as 'direct' | 'request')}
                      disabled={loading}
                      className="sr-only"
                    />
                    <div className={`px-4 py-3 border-2 rounded-lg transition-all ${
                      actionType === 'request'
                        ? 'border-red-600 bg-red-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}>
                      <span className="text-sm font-medium text-gray-900 block">
                        {t('deleteModal.submitRequest')}
                      </span>
                      <p className="text-xs text-gray-600 mt-1">
                        {t('deleteModal.submitRequestDesc')}
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            )}

            {/* Admin/Non-owner Info Notice */}
            {!success && !canDirectDelete && canRequestDelete && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm text-blue-700">
                      {t('deleteModal.adminNotice')}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="p-6 border-t border-gray-200 flex items-center justify-end gap-3">
            {success ? (
              <button
                type="button"
                onClick={handleClose}
                className="px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                {t('deleteModal.close')}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={loading}
                >
                  {t('deleteModal.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  disabled={loading}
                >
                  {loading && (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  )}
                  <span>
                    {loading
                      ? t('deleteModal.processing')
                      : actionType === 'direct'
                        ? t('deleteModal.deleteButton')
                        : t('deleteModal.requestButton')}
                  </span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    )
  }
)

DeleteProjectModal.displayName = 'DeleteProjectModal'

export default DeleteProjectModal
